import { CommonModule, DatePipe } from '@angular/common';
import { Component, ChangeDetectorRef, inject, OnInit, PLATFORM_ID } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AdminTemplatesComponent } from './templates/templates';
import { AdminAuditLogComponent } from './audit-log/audit-log';
import { ScholarshipApiService } from '../../core/api-services/scholarship-api.service';
import { PlatformService } from '../../core/services/platform.service';
import { TemplateApiService } from '../../core/api-services/template-api.service';
import { ScholarshipTemplate } from '../../core/models/scholarship-template';
import { Scholarship } from '../../core/models/scholarship';
import { AdminScholarshipsComponent } from "./scholarships/scholarships";
import { AdminScholarshipCycleComponent } from './scholarship-cycle/scholarship-cycle';
import { AdminDocumentsComponent } from './documents/documents';
import { AdminApplicationsComponent } from './applications/applications';
import { AdminAwardsComponent } from './awards/awards';
import { AdminClaimsComponent } from './claims/claims';
import { AdminExpensesComponent } from './expenses/expenses';
import { AdminFlightRequestsComponent } from './flight-requests/flight-requests';
import { AdminEventsComponent } from './events/events';
import { AdminProgressReportsComponent } from './progress-reports/progress-reports';
import { AdminReportsComponent } from './reports/reports';
import { AdminUsersComponent } from './users/users';
import { AdminSettingsComponent } from './settings/settings';
import { AuthorizationService } from '../../core/authorization/authorization.service';
import { Permissions } from '../../core/authorization/permissions';
import { ScholarshipApplicationApiService } from '../../core/api-services/scholarship-application-api.service';
import { AdminApplication } from '../../core/models/admin-application';

@Component({
  selector: 'app-admin',
  imports: [
    CommonModule,
    DatePipe,
    FormsModule,
    AdminTemplatesComponent,
    AdminAuditLogComponent,
    AdminScholarshipsComponent,
    AdminScholarshipCycleComponent,
    AdminApplicationsComponent,
    AdminDocumentsComponent,
    AdminAwardsComponent,
    AdminClaimsComponent,
    AdminExpensesComponent,
    AdminProgressReportsComponent,
    AdminReportsComponent,
    AdminFlightRequestsComponent,
    AdminEventsComponent,
    AdminUsersComponent
],
  templateUrl: './admin.html',
  styleUrl: './admin.scss',
})
export class AdminComponent implements OnInit {
  private readonly scholarshipApi = inject(ScholarshipApiService);
  private readonly platformService = inject(PlatformService);
  private readonly templateApi = inject(TemplateApiService);
  private readonly scholarshipApplicationApi = inject(ScholarshipApplicationApiService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  readonly authorization = inject(AuthorizationService);
  readonly permissions = Permissions;

  activeSection = 'dashboard';
  activeScholarshipTab: 'templates' | 'scholarships' | 'audit' = 'templates';

  /** Scholarship whose administration page is open, null on every other section. */
  selectedScholarshipId: number | null = null;

  /** Cycle shown on the scholarship administration page; empty means "current". */
  selectedScholarshipCycle = '';

  /**
   * Expense claims and the recurring payment queue are two tabs on the same
   * Expenses page, so the claim queue is not a separate destination.
   */
  activeExpenseTab: 'claims' | 'payments' = 'claims';

  savedTemplates: ScholarshipTemplate[] = [];
  recentTemplates: ScholarshipTemplate[] = [];
  savedScholarships: Scholarship[] = [];
  applications: AdminApplication[] = [];
  recentApplications: AdminApplication[] = [];
  applicationsLoading = false;
  applicationsError = false;

  ngOnInit(): void {
    this.clearLegacySeedData();

    this.route.queryParamMap.subscribe(params => {
      const section = params.get('section');

      // The scholarship administration page is addressed by ?scholarshipId=.
      const requestedScholarshipId = Number(params.get('scholarshipId'));
      this.selectedScholarshipId =
        Number.isFinite(requestedScholarshipId) && requestedScholarshipId > 0 ? requestedScholarshipId : null;
      this.selectedScholarshipCycle = params.get('cycle') ?? '';

      // Only the sections the user may actually open are reachable by URL, so a
      // notification link cannot land on a section the user has no permission for.
      const canOpenExpenses = (section === 'claims' || section === 'expenses')
        && this.authorization.hasAnyPermission(this.permissions.ClaimView, this.permissions.ExpenseView);

      const canOpenScholarshipCycle = section === 'scholarship-cycle'
        && !!this.selectedScholarshipId
        && this.authorization.hasAnyPermission(
          this.permissions.ScholarshipTemplateView,
          this.permissions.ScholarshipCreate,
          this.permissions.ScholarshipEdit,
          this.permissions.ScholarshipDelete);

      const canOpen =
        ((section === 'users' || section === 'settings') && this.authorization.hasPermission(this.permissions.UserManage))
        || (section === 'flights' && this.authorization.hasPermission(this.permissions.FlightRequestView))
        || (section === 'applications' && this.authorization.hasPermission(this.permissions.ApplicationReview))
        || (section === 'awards' && this.authorization.hasPermission(this.permissions.AwardView))
        || (section === 'reports' && this.authorization.hasPermission(this.permissions.ReportsView))
        || canOpenScholarshipCycle
        || canOpenExpenses;

      // "progress" reviews need award management, which is how reports are decided.
      const canOpenProgress = canOpen || (section === 'progress'
        && this.authorization.hasPermission(this.permissions.AwardView));

      if (section && (canOpen || canOpenProgress)) {
        // An explicit ?tab= selects which Expenses tab to open.
        const tab = params.get('tab');
        if (tab === 'claims' || tab === 'payments') {
          this.activeExpenseTab = tab;
        }

        this.showSection(section);
      } else {
        this.activeSection = 'dashboard';
      }
    });

    if (this.authorization.hasPermission(this.permissions.ScholarshipTemplateView)) {
      this.loadSavedTemplates();
    }
    if (this.authorization.hasAnyPermission(this.permissions.ScholarshipCreate, this.permissions.ScholarshipEdit, this.permissions.ScholarshipDelete)) {
      this.loadSavedScholarships();
    }
    if (this.authorization.hasPermission(this.permissions.ApplicationReview)) {
      this.loadRecentApplications();
    }
  }

  showSection(section: string): void {
    // "claims" is kept as an alias so existing links (and notification deep
    // links) land on the claim tab of the Expenses page.
    if (section === 'claims') {
      this.activeExpenseTab = 'claims';
      section = 'expenses';
    }

    this.activeSection = section;
  }

  /**
   * Opens the administration page for one scholarship: its budget summary,
   * awarded students and every application it received. The URL carries the id
   * so the page can be reloaded and shared.
   */
  openScholarship(scholarshipId: number): void {
    this.selectedScholarshipId = scholarshipId;
    this.selectedScholarshipCycle = '';
    this.activeSection = 'scholarship-cycle';

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { section: 'scholarship-cycle', scholarshipId },
    });
  }

  /** Returns from a scholarship's administration page to the scholarship list. */
  backToScholarships(): void {
    this.activeScholarshipTab = 'scholarships';

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { section: 'scholarship' },
    });
  }

  selectExpenseTab(tab: 'claims' | 'payments'): void {
    this.activeExpenseTab = tab;
  }

  selectScholarshipTab(tab: 'templates' | 'scholarships' | 'audit'): void {
    this.activeScholarshipTab = tab;
  }

  get activeScholarshipTemplatesCount(): number {
    return this.savedTemplates.filter(t => (t.fields?.length ?? 0) > 0).length;
  }

  get savedScholarshipsCount(): number {
    return this.savedScholarships?.length ?? 0;
  }

  get savedScholarshipsCountLabel(): string {
    return this.savedScholarships?.length === 0
      ? 'None created yet'
      : `${this.savedScholarships?.length ?? 0} scholarships created`;
  }

  get applicationsCount(): number {
    return this.applications.length;
  }

  /**
   * Whether anything is waiting in the reviewer queue. The menu entry for the
   * queue is hidden when there is nothing to review.
   */
  get hasApplicationsToReview(): boolean {
    return this.applications.length > 0;
  }

  getTemplateStatus(template: ScholarshipTemplate): 'COMPLETED' | 'DRAFT' {
    return template.fields?.length > 0 ? 'COMPLETED' : 'DRAFT';
  }

  private loadSavedTemplates(): void {
    this.templateApi.getAllScholarshipTemplates().subscribe(dt => {
      this.savedTemplates = dt;
      this.recentTemplates = this.savedTemplates.slice(0, 3);
      this.cdr.detectChanges();
    });
  }

  private loadSavedScholarships(): void {
    this.scholarshipApi.getScholarships().subscribe(dt => {
      this.savedScholarships = dt;
      this.cdr.detectChanges();
    });
  }

  private loadRecentApplications(): void {
    this.applicationsLoading = true;
    this.applicationsError = false;
    this.scholarshipApplicationApi.getApplicationsForReview().subscribe({
      next: applications => {
        this.applications = [...applications]
          .sort((a, b) => new Date(a.applicationDate).getTime() - new Date(b.applicationDate).getTime());
        this.recentApplications = this.applications.slice(0, 3);
        this.applicationsLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.applicationsLoading = false;
        this.applicationsError = true;
        this.cdr.detectChanges();
      },
    });
  }

  getApplicantName(application: AdminApplication): string {
    return [application.user?.firstName, application.user?.lastName].filter(Boolean).join(' ') || 'Unknown applicant';
  }

  private clearLegacySeedData(): void {
    if (!this.platformService.isBrowser)
      return;
  }
}
